import { useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import foldersReducer from '@app/reducers/entities/folders';
import useAsRef from '@app/hooks/use-as-ref';

type Props = {
  id: Oneflow.Folder['id'];
  enabled?: boolean;
};

export function useFolderFetch({ id, enabled = true }: Props) {
  const dispatch = useDispatch();
  // Store the id in a ref to have access to the latest value without re-rendering the hooks where the id is used
  const idRef = useAsRef(id);

  const folderFetchState = useSelector(
    (state) =>
      foldersReducer.getFetchSelector(state, {
        id,
      }),
    shallowEqual,
  );

  const folder = useSelector(
    (state) =>
      foldersReducer.getFolderSelector(state, {
        id,
      }),
    shallowEqual,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to include paramsRef.current in the dependencies as it is a ref
  const fetch = useCallback(() => {
    dispatch(
      foldersReducer.fetchFolder({
        id: idRef.current,
      }),
    );
  }, [
    dispatch,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the hook is reactive to the queryName
  useEffect(() => {
    if (!enabled || folderFetchState.success) {
      return;
    }
    fetch();
  }, [
    id,
    enabled,
    fetch,
  ]);

  return {
    fetch,
    data: folder,
    loading: folderFetchState.loading,
    error: folderFetchState.error,
    success: folderFetchState.success,
  };
}
